import { useState } from "react";
import { HeartPulse, ScanLine, Check, Camera, PenLine, Archive, ArrowRight, Plus, CalendarClock, QrCode } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { GoldButton } from "../components/GoldButton";
import { toast } from "sonner";
import { IMAGES, CARNET_ITEMS, ETAT_CATEGORIES, PRESTATIONS, PRESTATION_NOTE, CARNET_ENTRETIEN } from "../data/content";

const REPORT_FEATURES = [
  { icon: Camera, t: "Photos reliées", d: "Chaque photo est associée à l'élément contrôlé correspondant." },
  { icon: PenLine, t: "Signature & rapport", d: "Un rapport fidèle des éléments cochés et des observations." },
  { icon: Archive, t: "Archivage & comparaison", d: "Comparaison automatique avec l'état des lieux précédent." },
];

export default function Solutions() {
  const [extraItems, setExtraItems] = useState([]);
  const [draft, setDraft] = useState("");

  const addItem = () => {
    const v = draft.trim();
    if (!v) return;
    setExtraItems((s) => [...s, v]);
    setDraft("");
    toast.success("Élément ajouté à la check-list", { description: v });
  };

  return (
    <div data-testid="solutions-page">
      <Seo title="Carnet de Santé & états des lieux" path="/solutions"
        description="Carnet de Santé du Logement, carnet d'entretien et état des lieux intelligent : la mémoire numérique de votre bien par Speed Détresse." />
      <PageHero
        overline="Nos solutions phares"
        title={["Carnet de Santé &", "état des lieux intelligent"]}
        subtitle="Deux innovations au cœur de Speed Détresse : la mémoire permanente du logement et un état des lieux guidé, photographié et comparé."
        image={IMAGES.carnet}
        testid="solutions-hero"
      />

      {/* CARNET */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="flex items-center gap-3 text-gold"><HeartPulse className="h-6 w-6" strokeWidth={1.5} /><Overline>Le Carnet de Santé du Logement</Overline></div>
            <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">Le dossier médical de votre logement.</h2>
            <p className="mt-5 text-lg text-slate-300">Il conserve l'histoire de votre bien : ce qui a été fait, quand, pourquoi, par qui — et ce qui devra être vérifié ensuite.</p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {CARNET_ITEMS.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-slate-300">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                  </span>{c}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <img src={IMAGES.interior2} alt="Logement suivi" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CARNET D'ENTRETIEN */}
      <section className="relative z-10 border-t border-white/10 py-24" data-testid="carnet-entretien-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal className="order-2 lg:order-1">
              <div className="overflow-hidden rounded-3xl border border-white/10">
                <img src={IMAGES.interior3} alt="Suivi de l'entretien du logement" className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="order-1 lg:order-2">
              <div className="flex items-center gap-3 text-gold"><CalendarClock className="h-6 w-6" strokeWidth={1.5} /><Overline>Suivi régulier</Overline></div>
              <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">Le carnet d'entretien</h2>
              <p className="mt-5 text-lg text-slate-300">
                Volet du Carnet de Santé dédié à la maintenance : ce qui a été fait, ce qu'il reste à prévoir et quand.
                Un même produit, deux lectures — la mémoire complète du bien et le calendrier d'entretien.
              </p>
              <ul className="mt-8 space-y-3">
                {CARNET_ENTRETIEN.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold"><Check className="h-3 w-3" strokeWidth={2.5} /></span>{c}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* QR CODE PERMANENT */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24" data-testid="qr-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="flex items-center gap-3 text-gold"><QrCode className="h-6 w-6" strokeWidth={1.5} /><Overline>QR code permanent</Overline></div>
              <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">Un logement. Un QR code. Une histoire.</h2>
              <p className="mt-5 text-lg text-slate-300">
                Chaque logement peut être associé à un QR code permanent qui renvoie vers son espace.
                Les informations affichées évoluent sans jamais avoir à changer le QR code, et l'accès est adapté aux droits de chaque utilisateur.
              </p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {["Informations pratiques & consignes", "Signalement d'un problème", "Assistance occupant", "Informations utiles aux techniciens", "Documents autorisés", "Suivi en location saisonnière"].map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold"><Check className="h-3 w-3" strokeWidth={2.5} /></span>{i}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex items-center justify-center rounded-3xl border border-gold/25 bg-navy-elevated p-12">
                <div className="text-center">
                  <div className="mx-auto flex h-40 w-40 items-center justify-center rounded-3xl border-2 border-gold/50 bg-navy">
                    <QrCode className="h-24 w-24 text-gold" strokeWidth={1} />
                  </div>
                  <p className="mt-6 font-serif text-xl text-white">Le même QR code, toujours à jour</p>
                  <p className="mt-2 text-sm text-slate-400">Apposé près de l'entrée du logement</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ÉTAT DES LIEUX */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 text-gold"><ScanLine className="h-6 w-6" strokeWidth={1.5} /><Overline>Prestation guidée</Overline></div>
            <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">État des lieux intelligent</h2>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Une check-list complète, pièce par pièce, entièrement personnalisable. Ajoutez un élément et obtenez une nouvelle case à cocher.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {ETAT_CATEGORIES.map((cat, i) => (
              <Reveal key={cat.zone} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 bg-navy p-7">
                  <h3 className="font-serif text-2xl text-gold">{cat.zone}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {cat.items.map((it) => (
                      <li key={it} className="flex items-center gap-3 text-sm text-slate-300">
                        <span className="flex h-4 w-4 items-center justify-center rounded border border-gold/40" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}

            {/* Personnalisable */}
            <Reveal delay={0.18}>
              <div className="h-full rounded-2xl border border-dashed border-gold/40 bg-navy p-7">
                <h3 className="font-serif text-2xl text-white">Personnalisez</h3>
                <p className="mt-2 text-sm text-slate-400">Ajoutez vos propres éléments à contrôler.</p>
                <ul className="mt-4 space-y-2.5">
                  {extraItems.map((it, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-gold">
                      <span className="flex h-4 w-4 items-center justify-center rounded border border-gold bg-gold/20">
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>{it}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex gap-2">
                  <input
                    data-testid="etat-add-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addItem()}
                    placeholder="Ex : Interphone"
                    className="w-full rounded-lg border border-white/10 bg-navy-surface px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
                  />
                  <button
                    data-testid="etat-add-btn"
                    onClick={addItem}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gold text-navy transition-colors hover:bg-gold-light"
                    aria-label="Ajouter"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {REPORT_FEATURES.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-navy p-7">
                  <f.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  <h4 className="mt-4 font-serif text-xl text-white">{f.t}</h4>
                  <p className="mt-2 text-sm text-slate-400">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRESTATIONS PONCTUELLES */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal><Overline>Les prestations, enfin reliées</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">Du premier message à la porte refermée.</h2>
          <p className="mt-4 max-w-2xl text-slate-400">Une offre pensée pour les propriétaires bailleurs, les gestionnaires et tous ceux qui veulent protéger leur temps sans perdre le contrôle.</p>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRESTATIONS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-white/10 bg-navy-surface/60 p-7">
                <span className="font-serif text-5xl text-gold/25">0{i + 1}</span>
                <h3 className="mt-4 font-serif text-xl text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{p.desc}</p>
                <span className="mt-4 inline-block rounded-full border border-gold/40 px-3 py-1 text-xs text-gold">{p.tarif}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <p className="max-w-3xl rounded-2xl border border-white/10 bg-navy p-5 text-sm text-slate-400">{PRESTATION_NOTE}</p>
        </Reveal>
        <Reveal className="mt-12">
          <GoldButton to="/souscription" size="lg">Réserver une prestation <ArrowRight className="h-4 w-4" /></GoldButton>
        </Reveal>
      </section>
    </div>
  );
}
